// Theme interface - all colors used by D3 diagram components
export interface Theme {
    background: string;
    grid: string;
    classTitleBackground: string;
    classPropsBackground: string;
    classTitleBackgroundHover: string;
    classPropsBackgroundHover: string;
    classBorder: string;
    classBorderHover: string;
    classShadow: string;
    line: string;
    lineHighlight: string;
    lineGlow: string;
    lineDiamondFill: string;
    flowDotColor: string;
    anchorFill: string;
    anchorStroke: string;
    anchorHoverFill: string;
    anchorHoverStroke: string;
    anchorGlow: string;
    // Visual Vocabulary (force graph) colors
    vocabMarkerFill: string;
    vocabNodeText: string;
    vocabLinkLabel: string;
    // Metaball/liquid blob visualization colors
    metaballFill: string;
    metaballStroke: string;
}

export type ThemeName = 'dark' | 'light';

/**
 * Read a CSS custom property value from the document root.
 * Returns the computed value which automatically reflects dark/light mode.
 */
function getCssVar(name: string): string {
    if (typeof document === 'undefined') {
        // SSR fallback - return empty string
        return '';
    }
    return getComputedStyle(document.documentElement)
        .getPropertyValue(name).trim();
}

/**
 * Get the current theme by reading CSS variables.
 * The CSS variables automatically switch based on dark/light mode class on <html>.
 * The _name parameter is kept for backwards compatibility but is ignored -
 * the actual theme is determined by CSS variables which respond to the dark class.
 */
export function getTheme(_name?: ThemeName): Theme {
    return {
        background: getCssVar('--color-diagram-background'),
        grid: getCssVar('--color-diagram-grid'),
        classTitleBackground: getCssVar('--color-diagram-class-title'),
        classPropsBackground: getCssVar('--color-diagram-class-props'),
        classTitleBackgroundHover: getCssVar('--color-diagram-class-title-hover'),
        classPropsBackgroundHover: getCssVar('--color-diagram-class-props-hover'),
        classBorder: getCssVar('--color-diagram-class-border'),
        classBorderHover: getCssVar('--color-diagram-class-border-hover'),
        classShadow: getCssVar('--color-diagram-class-shadow'),
        line: getCssVar('--color-diagram-line'),
        lineHighlight: getCssVar('--color-diagram-line-highlight'),
        lineGlow: getCssVar('--color-diagram-line-glow'),
        lineDiamondFill: getCssVar('--color-diagram-line-diamond'),
        flowDotColor: getCssVar('--color-diagram-flow-dot'),
        anchorFill: getCssVar('--color-diagram-anchor-fill'),
        anchorStroke: getCssVar('--color-diagram-anchor-stroke'),
        anchorHoverFill: getCssVar('--color-diagram-anchor-hover-fill'),
        anchorHoverStroke: getCssVar('--color-diagram-anchor-hover-stroke'),
        anchorGlow: getCssVar('--color-diagram-anchor-glow'),
        vocabMarkerFill: getCssVar('--color-vocab-marker-fill'),
        vocabNodeText: getCssVar('--color-vocab-node-text'),
        vocabLinkLabel: getCssVar('--color-vocab-link-label'),
        metaballFill: getCssVar('--color-diagram-metaball-fill'),
        metaballStroke: getCssVar('--color-diagram-metaball-stroke'),
    };
}

// Default export - reads current theme from CSS variables
// Note: This is evaluated at module load time and won't update on theme change.
// Components should call getTheme() directly for dynamic updates.
const theme = getTheme();
export { theme };
export default theme;
